import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, User } from 'lucide-react';
import { client } from '../lib/sanity';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    client.fetch(`*[_type == "post"]{
      _id,
      title,
      slug,
      excerpt,
      "image": mainImage.asset->url,
      "author": author->name,
      readTime,
      "categories": categories[]->title
    }`).then((data) => {
    console.log("Sanity posts:", data);
    setPosts(data);
  }).catch((error) => {
    console.error("Sanity fetch error:", error);
  });
}, []);

  const categories = [
  'All',
  ...new Set(
    posts
      .flatMap(post => Array.isArray(post.categories) ? post.categories : [])
      .filter(category => category && category.trim() !== '')
  )
];
const filteredPosts =
  selectedCategory === 'All'
    ? posts
    : posts.filter(post =>
        Array.isArray(post.categories) &&
        post.categories.includes(selectedCategory)
      );
  return (
    <div>
      {/* Hero Section */}
      <section style={{ 
        background: 'var(--bg-secondary)',
        padding: '80px 0',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div className="container">
          <h1 className="hero-large" style={{ marginBottom: '16px' }}>
            Dental Tips & Insights
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Expert advice on maintaining optimal oral health
          </p>
        </div>
      </section>

      {/* Categories */}
      <section style={{ 
        padding: '40px 0',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div className="container">
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <span className="body-small" style={{ 
              padding: '12px 20px',
              color: 'var(--text-secondary)'
            }}>
              Categories:
            </span>
            {categories.map(category => (
  <button
    key={category}
    type="button"
    className="body-small"
    onClick={() => setSelectedCategory(category)}
    style={{
      padding: '8px 16px',
      background: selectedCategory === category ? 'var(--text-primary)' : 'var(--bg-secondary)',
      color: selectedCategory === category ? 'var(--bg-primary)' : 'var(--text-primary)',
      border: '1px solid var(--border-light)',
      borderRadius: '0px',
      cursor: 'pointer'
    }}
  >
    {category}
  </button>
))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="section-padding">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '32px'
          }}>
            {filteredPosts.map((post) => (
              <article 
                key={post._id}
                style={{
                  background: 'var(--bg-primary)',
                  borderRadius: '0px',
                  border: '1px solid var(--border-light)',
                  overflow: 'hidden',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                className="blog-card"
              >
                <img 
                  src={post.image || '/fallback-blog.jpg'}
                  alt={post.title}
                  style={{
                    width: '100%',
                    height: '220px',
                    objectFit: 'cover'
                  }}
                />
                <div style={{ padding: '32px' }}>
                  <div style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '0px',
                    marginBottom: '16px'
                  }}>
                    <span className="body-small" style={{ color: 'var(--text-primary)' }}>
                      {post.categories?.join(', ')}
                    </span>
                  </div>

                  <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                    {post.title}
                  </h3>

                  <p className="body-regular" style={{ 
                    marginBottom: '20px',
                    color: 'var(--text-secondary)'
                  }}>
                    {post.excerpt}
                  </p>

                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    marginBottom: '20px',
                    paddingTop: '20px',
                    borderTop: '1px solid var(--border-light)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <User size={14} style={{ color: 'var(--text-secondary)' }} />
                      <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {post.author}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Clock size={14} style={{ color: 'var(--text-secondary)' }} />
                      <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {post.readTime || '5 min read'}
                      </span>
                    </div>
                  </div>

                  <span className="btn-secondary" style={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    textDecoration: 'none'
                  }}>
                    <Link 
                      to={`/blog/${post.slug?.current}`}
                      style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        textDecoration: 'none',
                        color: 'inherit'
                      }}
                    >
                      Read Article
                      <ArrowRight size={16} />
                    </Link>
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section style={{ background: 'var(--bg-secondary)' }} className="section-padding">
        <div className="container">
          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 className="hero-medium" style={{ marginBottom: '16px' }}>
              Stay Informed
            </h2>
            <p className="body-large" style={{ 
              marginBottom: '32px',
              color: 'var(--text-secondary)'
            }}>
              Get the latest dental tips and health insights
            </p>
            <Link to="/contact" className="btn-primary">
              Contact Us for More Info
            </Link>
          </div>
        </div>
      </section>

      <style jsx>{`
        .blog-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        }
      `}</style>
    </div>
  );
};

export default Blog;
